import { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { api } from '../../utils/api';
import ReactQuill from 'react-quill-new';
import 'react-quill/dist/quill.snow.css';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Calendar, 
  Eye, 
  History,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Newspaper
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

export const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentNews, setCurrentNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    image_url: '',
    category: '',
    published: true,
    tags: []
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const res = await api.news.getAll(false); // false for publishedOnly
      setNews(res.data || []);
    } catch (error) {
      toast.error('Error al cargar noticias');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setCurrentNews(item);
      setFormData({
        ...item,
        tags: item.tags || []
      });
    } else {
      setCurrentNews(null);
      setFormData({
        title: '',
        summary: '',
        content: '',
        image_url: '',
        category: '',
        published: true,
        tags: []
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentNews) {
        await api.news.update(currentNews.id, formData);
        toast.success('Noticia actualizada');
      } else {
        await api.news.create(formData);
        toast.success('Noticia publicada');
      }
      setIsModalOpen(false);
      fetchNews();
    } catch (error) {
      toast.error('Error al procesar la noticia');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar esta entrada permanentemente?')) {
      try {
        await api.news.delete(id);
        toast.success('Señal eliminada');
        fetchNews();
      } catch (error) {
        toast.error('Error al eliminar');
      }
    }
  };

  const filteredNews = news.filter(item => 
    item.title?.toLowerCase().includes(search.toLowerCase()) ||
    item.category?.toLowerCase().includes(search.toLowerCase())
  );

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  return (
    <AdminLayout>
      <div className="space-y-12">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <span className="text-[#C6FF00] font-black uppercase tracking-[0.3em] text-[10px] block mb-2">Signal Broadcast</span>
            <h1 className="text-4xl font-black tracking-tighter uppercase italic">Gestión de Noticias</h1>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-3 px-8 py-4 bg-[#C6FF00] text-black font-black uppercase tracking-widest text-[10px] rounded-2xl hover:scale-105 transition-all shadow-[0_0_30px_rgba(198,255,0,0.2)]"
          >
            <Plus size={16} />
            Escribir Noticia
          </button>
        </header>

        <div className="relative">
          <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20" size={20} />
          <input
            type="text"
            placeholder="FILTRAR TRANSMISIONES..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white/[0.02] border border-white/5 rounded-2xl py-6 pl-16 pr-6 text-white text-xs font-black uppercase tracking-[0.2em] focus:border-[#C6FF00]/40 outline-none transition-all"
          />
        </div>

        <div className="grid grid-cols-1 gap-6">
          {filteredNews.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="group p-8 rounded-[2rem] bg-white/[0.02] border border-white/5 hover:border-[#C6FF00]/20 transition-all flex flex-col md:flex-row items-center gap-10"
            >
              <div className="w-full md:w-32 h-32 rounded-2xl overflow-hidden bg-white/5 border border-white/10 shrink-0">
                <img src={item.image_url || 'https://via.placeholder.com/150'} alt="" className="w-full h-full object-cover opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" />
              </div>
              
              <div className="flex-grow space-y-4">
                <div className="flex items-center gap-4">
                  <span className="text-[#C6FF00] text-[10px] font-black uppercase tracking-widest">{item.category || 'GENERAL'}</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-white/10"></div>
                  <span className="text-white/30 text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                    <Calendar size={12} />
                    {new Date(item.created_at).toLocaleDateString()}
                  </span>
                </div>
                <h3 className="text-2xl font-black uppercase tracking-tight text-white group-hover:text-[#C6FF00] transition-colors">{item.title}</h3>
                <p className="text-white/40 text-sm font-medium line-clamp-2 max-w-2xl">{item.summary}</p>
                <div className="flex items-center gap-6 pt-4">
                   <div className="flex items-center gap-2">
                     <div className={`w-1.5 h-1.5 rounded-full ${item.published ? 'bg-[#C6FF00]' : 'bg-red-500'}`}></div>
                     <span className="text-[8px] font-black uppercase tracking-widest text-white/20 whitespace-nowrap">
                       {item.published ? 'Publicado' : 'Borrador'}
                     </span>
                   </div>
                </div>
              </div>

              <div className="flex md:flex-col gap-3 shrink-0">
                <button 
                  onClick={() => handleOpenModal(item)}
                  className="p-4 bg-white/5 rounded-2xl text-white/40 hover:text-[#C6FF00] hover:bg-[#C6FF00]/10 border border-transparent hover:border-[#C6FF00]/20 transition-all"
                >
                  <Edit2 size={18} />
                </button>
                <button 
                  onClick={() => handleDelete(item.id)}
                  className="p-4 bg-white/5 rounded-2xl text-white/40 hover:text-red-500 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 transition-all"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Modal News Rebuild */}
        {isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              className="absolute inset-0 bg-black/95 backdrop-blur-xl" 
              onClick={() => setIsModalOpen(false)} 
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-[#0a0a0a] border border-white/5 rounded-[3rem] p-12"
            >
              <form onSubmit={handleSubmit} className="space-y-12">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                  <div className="lg:col-span-2 space-y-8">
                    <div className="space-y-4">
                      <label className="text-[10px] font-black uppercase tracking-widest text-white/40 italic">Título de la Señal</label>
                      <input
                        required
                        value={formData.title}
                        onChange={e => setFormData({...formData, title: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 text-2xl font-black text-white outline-none focus:border-[#C6FF00]/40 transition-all"
                        placeholder="INGRESE EL ENCABEZADO..."
                      />
                    </div>

                    <div className="space-y-4">
                      <label className="text-[10px] font-black uppercase tracking-widest text-white/40 italic">Resumen Ejecutivo</label>
                      <textarea
                        rows={3}
                        value={formData.summary}
                        onChange={e => setFormData({...formData, summary: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-6 text-white outline-none focus:border-[#C6FF00]/40 resize-none font-medium"
                      />
                    </div>

                    <div className="space-y-4">
                      <label className="text-[10px] font-black uppercase tracking-widest text-white/40 italic">Contenido de la Transmisión</label>
                      <div className="rounded-2xl border border-white/10 overflow-hidden bg-white/[0.02]">
                        <ReactQuill
                          theme="snow"
                          value={formData.content}
                          onChange={content => setFormData({...formData, content})}
                          modules={quillModules}
                          className="quill-dark ql-admin"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-8">
                    <div className="p-8 rounded-[2.5rem] bg-white/[0.02] border border-white/5 space-y-8">
                      <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-widest text-white/40 italic">Imagen Principal (URL)</label>
                        <input
                          value={formData.image_url}
                          onChange={e => setFormData({...formData, image_url: e.target.value})}
                          className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-xs text-white outline-none focus:border-[#C6FF00]/40"
                        />
                        {formData.image_url && (
                          <div className="aspect-video rounded-xl overflow-hidden border border-white/10">
                            <img src={formData.image_url} className="w-full h-full object-cover" alt="Preview" />
                          </div>
                        )}
                      </div>

                      <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-widest text-white/40 italic">Categoría</label>
                        <select
                          value={formData.category}
                          onChange={e => setFormData({...formData, category: e.target.value})}
                          className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-xs text-white outline-none focus:border-[#C6FF00]/40 appearance-none uppercase font-black"
                        >
                          <option value="">GENERAL</option>
                          <option value="TECNOLOGÍA">TECNOLOGÍA</option>
                          <option value="NEGOCIOS">NEGOCIOS</option>
                          <option value="IA">IA / ML</option>
                          <option value="CYBERSECURITY">CYBERSECURITY</option>
                        </select>
                      </div>

                      <div className="pt-8 border-t border-white/5 space-y-6">
                        <button
                          type="button"
                          onClick={() => setFormData({...formData, published: !formData.published})}
                          className={`w-full p-6 rounded-2xl border transition-all flex items-center justify-between group ${
                            formData.published ? 'bg-[#C6FF00]/10 border-[#C6FF00]/40 text-[#C6FF00]' : 'bg-white/5 border-white/5 text-white/20'
                          }`}
                        >
                          <span className="text-[10px] font-black uppercase tracking-widest">Publicar</span>
                          {formData.published ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col gap-4">
                      <button
                        type="submit"
                        className="w-full py-6 bg-[#C6FF00] text-black rounded-2xl font-black uppercase tracking-widest text-center text-xs hover:scale-105 transition-all shadow-[0_0_50px_rgba(198,255,0,0.3)]"
                      >
                        Transmitir Señal
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsModalOpen(false)}
                        className="w-full py-6 bg-white/5 text-white/40 border border-white/5 rounded-2xl font-black uppercase tracking-widest text-center text-[10px] hover:bg-white/10 transition-all"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>

      <style>{`
        .ql-admin .ql-toolbar { background: rgba(255,255,255,0.03); border: none !important; border-bottom: 1px solid rgba(255,255,255,0.05) !important; padding: 1.5rem !important; }
        .ql-admin .ql-container { border: none !important; color: rgba(255,255,255,0.7); min-height: 400px; font-family: 'DM Sans', sans-serif; }
        .ql-admin .ql-editor { padding: 2.5rem !important; font-size: 1.1rem; line-height: 1.8; }
        .ql-admin .ql-stroke { stroke: rgba(255,255,255,0.4) !important; }
        .ql-admin .ql-picker { color: rgba(255,255,255,0.4) !important; }
        .ql-admin .ql-active .ql-stroke { stroke: #C6FF00 !important; }
        .ql-admin .ql-active { color: #C6FF00 !important; }
        .ql-admin .ql-picker-options { background: #080808 !important; border: 1px solid rgba(255,255,255,0.1) !important; border-radius: 1rem; }
      `}</style>
    </AdminLayout>
  );
};
